import { FunctionComponent, useEffect, useState } from 'react';
import * as yup from "yup";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SearchableInputsDropdown from '../common/SearchableDropdown';
import MemberAvailabilityDropdown from '../common/MemberAvailabilityDropdown';
import closeIcon from "../../assets/task/close.png";
import InputsNew from '../common/InputsNew';
import DateSelector from '../common/DateSelector';
import { addDays, addHours, addMinutes, format } from 'date-fns';
 import PlusIcon from "../../assets/task/plusicontask.svg";
import { showError } from '../common/ToastNotification';

// Types for member tasks
interface MemberTask {
    id: string;
    taskType: string;
    taskID: string;
    taskName: string;
    taskChecklist: string[];
    taskSkill: string; // Single skill name for this task
    taskSkillID: string; // Single skill ID for this task
    startTaskDate: string | null;
    durationDays: number;
    durationTimeHour: number;
    durationTimeMinute: number;
    parentTask?: string;
    parentTaskID?: string;
    endDate?: string | null;
}

interface AddMemberToGoalPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onAddMember: (memberData: any) => void;
    availableMembers: { key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[];
    goalTypeData: any;
    goalStartDate: string;
    goalEndDate: string;
    editingMember?: { key: string; name: string } | null;
    existingTasks?: any[];
    allProjectMembers?: { key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[];
    existingGoalMembers?: { key: string; name: string }[];
    goalSkills?: any[]; // Goal level skills
    parentTaskOptions?: { key: string; name: string }[]; // Parent task options for existing goals
}

const AddMemberToGoalPopup: FunctionComponent<AddMemberToGoalPopupProps> = ({
    isOpen,
    onClose,
    onAddMember,
    availableMembers,
    goalTypeData,
    goalStartDate,
    goalEndDate,
    editingMember,
    existingTasks,
    allProjectMembers,
    existingGoalMembers,
    goalSkills = [],
    parentTaskOptions = []
}) => {
    const [selectedMember, setSelectedMember] = useState<{ key: string; name: string } | null>(null);
    const [memberTasks, setMemberTasks] = useState<MemberTask[]>([]);
    const [taskTypeOptions, setTaskTypeOptions] = useState<{ key: string; name: string }[]>([]);
    const [availableMembersForEdit, setAvailableMembersForEdit] = useState<{ key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[]>([]);

    // Validation schema
    const validationSchema = yup.object().shape({
        member: yup.string().required('Please select a member'),
    });

    const {
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        reset
    } = useForm<any>({
        resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
        if (goalTypeData?.tasks) {
            const tasks = goalTypeData.tasks.map((task: any) => ({
                key: task.id,
                name: task.name,
                type: task?.role_name
            }));
            setTaskTypeOptions(tasks);
        }
    }, [goalTypeData]);

    // Calculate available members for editing
    useEffect(() => {
        if (editingMember && allProjectMembers) {
            // For EditGoal, include ALL project members (including existing goal members)
            // This allows reassigning tasks between existing members
            setAvailableMembersForEdit(allProjectMembers);
        }
    }, [editingMember, allProjectMembers, existingGoalMembers]);

    // Initialize selectedMember when popup opens for editing
    useEffect(() => {
        if (isOpen && editingMember && !selectedMember) {
            setSelectedMember(editingMember);
            setValue('member', editingMember.name);
        }
    }, [isOpen, editingMember]);

    useEffect(() => {
        if (editingMember) {
            // If editing existing member, set the member and load existing tasks ONLY on initial load
            if (!selectedMember) {
                setSelectedMember(editingMember);
                setValue('member', editingMember.name);
            }
            if (existingTasks && existingTasks.length > 0) {
                setMemberTasks(existingTasks);
            } else {
                setMemberTasks([createNewTask()]);
            }
        } else if (selectedMember) {
            // Initialize with one task when member is selected
            setMemberTasks([createNewTask()]);
        } else {
            setMemberTasks([]);
        }
    }, [editingMember, existingTasks]); // Removed selectedMember from dependencies

    const createNewTask = (): MemberTask => ({
        id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
        taskType: '',
        taskID: '',
        taskName: '',
        taskChecklist: [],
        taskSkill: '',
        taskSkillID: '',
        startTaskDate: null,
        durationDays: 1,
        durationTimeHour: 0,
        durationTimeMinute: 0,
        endDate: null
    });

    const addTask = () => {
        setMemberTasks(prev => [...prev, createNewTask()]);
    };

    const removeTask = (taskId: string) => {
        if (memberTasks.length > 1) {
            setMemberTasks(prev => prev.filter(task => task.id !== taskId));
        }
    };

    const updateTask = (taskId: string, field: keyof MemberTask, value: any) => {
        setMemberTasks(prev => prev.map(task =>
            task.id === taskId ? { ...task, [field]: value } : task
        ));
    };

    const calculateEndDate = (startDate: string, durationDays: number, durationHours: number, durationMinutes: number) => {
        if (!startDate || (!durationDays && !durationHours && !durationMinutes)) {
            return null;
        }

        // Parse date parts manually to avoid UTC timezone shift
        const [year, month, day] = startDate.split('-').map(Number);
        const startDateObj = new Date(year, month - 1, day, 0, 0, 0, 0);

        let endDateObj = addMinutes(startDateObj, durationMinutes);
        endDateObj = addHours(endDateObj, durationHours);
        endDateObj = addDays(endDateObj, (durationDays - 1));

        return format(endDateObj, "MM-dd-yyyy");
    };

    const getTaskChecklistForTask = (taskTypeId: string) => {
        if (!goalTypeData?.tasks || !taskTypeId) return [];

        const selectedTask = goalTypeData.tasks.find((task: any) => task.id == taskTypeId);

        if (selectedTask) {
            return selectedTask.checklists.map((item: any) => ({
                key: parseInt(item.id),
                name: item.name
            }));
        }
        return [];
    };

    // Task skill handler function (single selection)
    const handleSelectTaskSkill = (taskId: string, key: any, name: any) => {
        setMemberTasks(prev => prev.map(task =>
            task.id === taskId
                ? { ...task, taskSkill: name, taskSkillID: key.toString() }
                : task
        ));
    };

    const validateTasks = (): boolean => {
        if (memberTasks.length === 0) {
            showError('Please add at least one task');
            return false;
        }

        for (const task of memberTasks) {
            if (!task.taskType) {
                showError('Please select task type for all tasks');
                return false;
            }

            if (!task.taskName || task.taskName.trim() === '') {
                showError('Please enter task name for all tasks');
                return false;
            }

            if (!task.startTaskDate) {
                showError('Please select start date for all tasks');
                return false;
            }

            if (task.taskChecklist.length === 0) {
                showError('Please select at least one checklist item for all tasks');
                return false;
            }

            if (!task.taskSkillID) {
                showError('Please select a skill for all tasks');
                return false;
            }

            const totalDurationMinutes = (task.durationTimeHour * 60) + task.durationTimeMinute;
            if (totalDurationMinutes < 15 && task.taskType !== "Meeting / Knowledge Transfer") {
                showError('Total duration must be at least 15 minutes for all tasks');
                return false;
            }
        }
        return true;
    };

    const handleSelectMember = (key: string, name: string) => {
        setSelectedMember({ key, name });
        setValue('member', name);
        trigger('member');
    };

    const onSubmit = () => {
        const currentMember = selectedMember; // Always use selectedMember as it gets updated when user changes selection
        if (!currentMember) {
            showError('Please select a member');
            return;
        }

        if (!validateTasks()) {
            return;
        }

        onAddMember({
            member: currentMember,
            tasks: memberTasks
        });

        // Reset form
        reset();
        setSelectedMember(null);
        setMemberTasks([]);
    };

    const handleClose = () => {
        reset();
        setSelectedMember(null);
        setMemberTasks([]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 addmember-model">
            <div className=" dark:bg-gray-800 rounded-lg pt-3 p-6 w-full max-w-4xl max-h-[75vh] overflow-y-auto inner-addmember-model">
                <div className="flex items-center justify-between mb-2 sticky top-[-10px] z-10 headermember-dark dark: top-[-20px]">
                    <h2 className="text-xl font-bold dark:text-white">
                        {editingMember ? 'Edit Member Tasks' : 'Add Member to Goal'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="rounded-[100px] bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white"
                    >
                        <img src={closeIcon} alt="Close" className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Member Selection */}
                     <div className="grid grid-cols-1 mq1400:grid-1 mq1275:grid-cols-1 mq950:grid-cols-1 mq575:grid-cols-1 mt-[8px] gap-[16px] min-w-full">
                        {editingMember ? (
                            <MemberAvailabilityDropdown
                                label="Change Member*"
                                initialValue={selectedMember?.name || editingMember.name}
                                placeHolder="Choose a member"
                                options={availableMembersForEdit}
                                onSelect={handleSelectMember}
                                errorMessage={errors?.member}
                            />
                        ) : (
                            <MemberAvailabilityDropdown
                                label="Select Member*"
                                initialValue={selectedMember?.name || ''}
                                placeHolder="Choose a member"
                                options={availableMembers}
                                onSelect={handleSelectMember}
                                errorMessage={errors?.member}
                            />
                        )}
                    </div>

                    {/* Tasks Section */}
                    {(selectedMember || editingMember) && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold dark:text-white my-0">
                                    Tasks for {selectedMember?.name}
                                </h3>
                                <button
                                    type="button"
                                    onClick={addTask}
                                    className="py-1 px-1 mq575:h-[35px]  mq575:py-[12.5px] mq575:px-[25px] addmembertask-btn text-white text-sm rounded plus-task-btn"
                                >
                                   
                                   <img src={PlusIcon} className="w-5 h-5" alt="" />
                                </button>
                            </div>

                            {memberTasks.map((task, taskIndex) => (
                                <div key={task.id} className="border border-gray-300 dark:border-gray-600 rounded-md p-4 bg-gray-50 dark:bg-gray-900 model-form-member-dv">
                                    <div className="flex items-center justify-between mb-4">
                                        <h5 className="my-0 font-medium dark:text-white">Task {taskIndex + 1}</h5>
                                        {memberTasks.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeTask(task.id)}
                                                className="rounded-[100px] bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white"
                                            >
                                                  <img src={closeIcon} alt="Close" className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 mt-[8px] gap-[16px] min-w-full">
                                        {/* Task Type */}
                                        <div>
                                            <SearchableInputsDropdown
                                                label="Task Type*"
                                                initialValue={task.taskType}
                                                placeHolder="Select Task Type"
                                                options={taskTypeOptions}
                                                onSelect={(key: number, name: string) => {
                                                    updateTask(task.id, 'taskType', name);
                                                    updateTask(task.id, 'taskID', key);
                                                    updateTask(task.id, 'taskChecklist', []);

                                                    // For goal ID 55: auto-fill task name and pre-select all checklists
                                                    if (goalTypeData?.id === "55") {
                                                        updateTask(task.id, 'taskName', name);
                                                        const availableChecklists = getTaskChecklistForTask(key.toString());
                                                        if (availableChecklists.length > 0) {
                                                            updateTask(task.id, 'taskChecklist', availableChecklists.map((item: any) => item.key.toString()));
                                                        }
                                                    }

                                                    // Auto-prefill for Meeting / Knowledge Transfer
                                                    if (name === "Meeting / Knowledge Transfer") {
                                                        updateTask(task.id, 'durationDays', 1);
                                                        updateTask(task.id, 'durationTimeHour', 0);
                                                        updateTask(task.id, 'durationTimeMinute', 0);
                                                        
                                                        // Auto-select all checklist items for this task type
                                                        const availableChecklists = getTaskChecklistForTask(key.toString());
                                                        if (availableChecklists.length > 0) {
                                                            updateTask(task.id, 'taskChecklist', availableChecklists.map((item: any) => item.key.toString()));
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>

                                        {/* Task Name */}
                                        <div>
                                            <InputsNew
                                                label="Task Name*"
                                                type="text"
                                                contentPlaceholder="Enter task name"
                                                value={task.taskName}
                                                onChange={(e) => updateTask(task.id, 'taskName', e.target.value)}
                                                disabled={goalTypeData?.id === "55"}
                                            />
                                        </div>

                                        {/* Start Date */}
                                        <div>
                                            <DateSelector
                                                label="Start Date*"
                                                selectedDate={task.startTaskDate}
                                                onChange={(selectedDate: string | null) => {
                                                    // Validate date is within goal range
                                                    if (goalStartDate && goalEndDate && selectedDate) {
                                                        const taskStart = new Date(selectedDate);
                                                        const goalStart = new Date(goalStartDate);
                                                        const goalEnd = new Date(goalEndDate);

                                                        taskStart.setHours(0, 0, 0, 0);
                                                        goalStart.setHours(0, 0, 0, 0);
                                                        goalEnd.setHours(23, 59, 59, 999);

                                                        // Skip validation if selected date is today
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        const isToday = taskStart.getTime() === today.getTime();

                                                        if (!isToday) {
                                                            if (taskStart < goalStart) {
                                                                showError(`Task start date cannot be before goal start date (${goalStartDate})`);
                                                                return;
                                                            }

                                                            if (taskStart > goalEnd) {
                                                                showError(`Task start date cannot be after goal end date (${goalEndDate})`);
                                                                return;
                                                            }
                                                        }
                                                    }

                                                    updateTask(task.id, 'startTaskDate', selectedDate);
                                                    const endDate = calculateEndDate(selectedDate || '', task.durationDays, task.durationTimeHour, task.durationTimeMinute);
                                                    updateTask(task.id, 'endDate', endDate);
                                                }}
                                                nothideParsedValue={true}
                                                blueIcon={true}
                                                showAllDate={true}
                                            />
                                        </div>
                                    </div>

                                    {/* Duration */}
                                    <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 mt-[8px] gap-[16px] min-w-full">
                                        <div>
                                            <InputsNew
                                                label="Days*"
                                                type="number"
                                                contentPlaceholder="1"
                                                value={task.durationDays.toString()}
                                                onChange={(e) => {
                                                    const days = Math.max(1, parseInt(e.target.value) || 1);
                                                    updateTask(task.id, 'durationDays', days);
                                                    const endDate = calculateEndDate(task.startTaskDate || '', days, task.durationTimeHour, task.durationTimeMinute);
                                                    updateTask(task.id, 'endDate', endDate);
                                                }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div>
                                            <InputsNew
                                                label="Hours*"
                                                type="number"
                                                contentPlaceholder="0"
                                                defaultValue={task.durationTimeHour.toString()}
                                                onChange={(e) => {
                                                    const hours = Math.min(23, Math.max(0, parseInt(e.target.value) || 0));
                                                    updateTask(task.id, 'durationTimeHour', hours);
                                                    const endDate = calculateEndDate(task.startTaskDate || '', task.durationDays, hours, task.durationTimeMinute);
                                                    updateTask(task.id, 'endDate', endDate);
                                                }}
                                                disabled={task.taskType === "Meeting / Knowledge Transfer"}
                                            />
                                        </div>
                                        <div>
                                            <InputsNew
                                                label="Minutes*"
                                                type="number"
                                                contentPlaceholder="0"
                                                defaultValue={task.durationTimeMinute.toString()}
                                                onChange={(e) => {
                                                    const minutes = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                                                    updateTask(task.id, 'durationTimeMinute', minutes);
                                                    const endDate = calculateEndDate(task.startTaskDate || '', task.durationDays, task.durationTimeHour, minutes);
                                                    updateTask(task.id, 'endDate', endDate);
                                                }}
                                                disabled={task.taskType === "Meeting / Knowledge Transfer"}
                                            />
                                        </div>
                                    </div>

                                    {/* End Date Display */}
                                    <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 mt-[8px] gap-[16px] min-w-full">
                                        <div className="grid gap-[4px]">
                                            <label className="block text-sm font-semibold mb-2 dark:text-white">End Date</label>
                                        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded border">
                                            <span className="text-sm dark:text-white">
                                                {task.endDate || '-'}
                                            </span>
                                        </div>
                                        </div>
                                    </div>

                                    {/* Task Checklist */}
                                    {task.taskType && (
                                        <div className="mb-4">
                                            <label className="block text-sm font-semibold mb-2 dark:text-white">Task Checklist*</label>
                                            {(() => {
                                                const availableChecklists = getTaskChecklistForTask(task.taskID);
                                                if (availableChecklists.length === 0) {
                                                    return (
                                                        <div className="p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded">
                                                            <p className="text-red-600 dark:text-red-400 text-sm">
                                                                No checklist available for this task type
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="border border-gray-300 dark:border-gray-600 rounded p-3">
                                                        <div className="mb-2">
                                                            <label className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={availableChecklists.length > 0 && availableChecklists.every((item: any) => task.taskChecklist.includes(item.key.toString()))}
                                                                    onChange={(e) => {
                                                                        if (goalTypeData?.id === "55") return;
                                                                        if (e.target.checked) {
                                                                            updateTask(task.id, 'taskChecklist', availableChecklists.map((item: any) => item.key.toString()));
                                                                        } else {
                                                                            updateTask(task.id, 'taskChecklist', []);
                                                                        }
                                                                    }}
                                                                    disabled={goalTypeData?.id === "55"}
                                                                />
                                                                <span className="font-semibold text-sm dark:text-white">Select All</span>
                                                            </label>
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {availableChecklists.map((item: any) => (
                                                                <label key={item.key} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={task.taskChecklist.includes(item.key.toString())}
                                                                        onChange={(e) => {
                                                                            if (goalTypeData?.id === "55") return;
                                                                            const currentChecklist = task.taskChecklist;
                                                                            if (e.target.checked) {
                                                                                updateTask(task.id, 'taskChecklist', [...currentChecklist, item.key.toString()]);
                                                                            } else {
                                                                                updateTask(task.id, 'taskChecklist', currentChecklist.filter((id: string) => id !== item.key.toString()));
                                                                            }
                                                                        }}
                                                                        disabled={goalTypeData?.id === "55"}
                                                                    />
                                                                    <span className="text-sm dark:text-white">{item.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/* Task Skill (Single Selection) */}
                                    <div className="mb-4">
                                        <SearchableInputsDropdown
                                            label="Task Skill"
                                            initialValue={task.taskSkill}
                                            placeHolder={goalSkills.length === 0 ? "Please select goal skills first" : "Choose"}
                                            options={goalSkills}
                                            onSelect={(key: any, name: any) => handleSelectTaskSkill(task.id, key, name)}
                                        />
                                    </div>

                                    {/* Parent Task (for EXISTING goals) */}
                                    <div className="mb-4">
                                        <SearchableInputsDropdown
                                            label="Parent Task"
                                            initialValue={task.parentTask || ''}
                                            placeHolder={parentTaskOptions.length === 0 ? "No tasks available" : "Choose"}
                                            options={parentTaskOptions}
                                            onSelect={(key: any, name: any) => {
                                                updateTask(task.id, 'parentTask', name);
                                                updateTask(task.id, 'parentTaskID', key);
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                        >
                            <span className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block"> Cancel</span>
                           
                        </button>
                        <button
                            type="submit"
                            className="py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] text-sm block cursor-pointer flex-col items-center justify-start p-[10px] plus-task-btn taskdash-plush-img rounded gap-[4px]  text-white"
                        >
                            {editingMember ? 'Update Tasks' : 'Add Member'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddMemberToGoalPopup;